import { useCallback, useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector, useLogout } from '@pkg/hooks';
import { LocalStorageKeys } from '@pkg/types';

import { useUser } from '@app/store/zustand';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

// This hook is used to handle any redirects regarding the index pages
// after signing in: Dashboard & Pods homepage (/yativerse & /profile)
// There are a few non-global redirects that need to happen,
// with an order of priority. Specifically:
// - Pending transfer
// - Sign user out if they've come directly to an index page after
//   being on `/create` logged out and having a draft user created.
//   - This ^ case can possibly be removed since we've added
//     `isRegisteredUser` to the Protected/Public route components,
//     but leaving it in for now as the redux store some time lags
//     a bit and we miss that on/before sign in.

export function useIndexRedirects() {
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const { isLoading: isUserLoading } = useUser();
    const { isAuthenticated } = useAuthTokens();
    const isRegisteredUser = useAppSelector((state) => state.user.isRegisteredUser);
    const userPendingIncoming = useAppSelector((state) => state.transfers.userPendingIncoming);

    const { logout } = useLogout();
    const handleDraftUser = useCallback(() => {
        localStorage.setItem(LocalStorageKeys.RemoveDraftUser, '0');
        logout();
    }, [logout]);

    useEffect(() => {
        const shouldLogOut = Boolean(Number(localStorage.getItem(LocalStorageKeys.RemoveDraftUser)) || 0);
        if (isRegisteredUser || isUserLoading || pathname !== '/') return;
        if (isAuthenticated && shouldLogOut) {
            handleDraftUser();
        }
    }, [handleDraftUser, isAuthenticated, isRegisteredUser, isUserLoading, pathname]);

    useEffect(() => {
        if (userPendingIncoming) {
            navigate(`/transfer/${userPendingIncoming.eid}`);
        }
    }, [navigate, userPendingIncoming]);
}
